import { useMemo } from "react";

// Vera ruota della fortuna in SVG: uno spicchio per partecipante (arancione/nero),
// lucine che lampeggiano sul bordo, lancetta in alto e logo al centro.
// La ruota cresce con gli iscritti e resta sempre dentro lo schermo (maxWidth 100%).
// I nomi sono SEMPRE interi e stanno DENTRO lo spicchio: ancorati a 2px dal
// bordo esterno, crescono verso il CENTRO. I nickname normali (10-16 lettere)
// stanno nella parte larga e leggibile; solo quelli molto lunghi arrivano verso
// il centro. Il font e' calcolato perche' 16 caratteri ci stiano sempre.
// `rotation` in gradi orari (guidata dall'estrazione). Tra uno spicchio e l'altro
// ci sono le BACCHETTE: la lancetta in alto le colpisce fisicamente.
// La lancetta e' una vera MOLLA-SMORZATORE simulata (spring-damper): quando la
// ruota animata (GiveawayWheel) guida, riceve `pointerBend` gia' integrato
// frame per frame (impulso della bacchetta, rilascio, vibrazione organica).
// In uso statico (iscrizioni) ripiega sulla formula geometrica: piega crescente
// mentre una bacchetta le passa sotto, scatto oltre il bordo.
export function FortuneWheel({ names, rotation = 0, winnerIndex = -1, landed = false, size = 620, pointerBend = null }) {
  const n = names.length;
  const seg = n ? 360 / n : 360;
  const C = 150;      // centro (viewBox 300: la ruota sta in mezzo, attorno spazio per i nomi)
  const R = 102;      // raggio spicchi
  const showNames = n > 0 && n <= 60;
  const maxLen = n ? Math.max(...names.map((x) => x.length)) : 0;
  // il nome deve stare DENTRO lo spicchio: il font tiene conto di quanti sono
  // (altezza dell'arco) e della lunghezza radiale disponibile (~88 unita': dal
  // bordo meno 2px fino al perno centrale). Base minima 16 caratteri: nickname
  // normali sempre leggibili; si riduce solo se ci sono nomi piu' lunghi
  const fontSize = Math.max(5.5, Math.min(11, 316 / Math.max(n, 8), 88 / (0.62 * Math.max(maxLen, 16))));
  // crescita graduale: parte piccola (220px con 1 iscritto) e cresce a ogni
  // iscrizione per tenere i nomi leggibili, ma con TETTO MASSIMO (size) oltre
  // il quale non va mai: con 100+ iscritti resta in scena senza esagerare
  const cap = Math.min(size, 220 + n * 14);

  const slices = useMemo(() => names.map((name, i) => {
    const a0 = ((i * seg) - 90) * Math.PI / 180;
    const a1 = (((i + 1) * seg) - 90) * Math.PI / 180;
    const x0 = C + R * Math.cos(a0), y0 = C + R * Math.sin(a0);
    const x1 = C + R * Math.cos(a1), y1 = C + R * Math.sin(a1);
    const d = `M${C},${C} L${x0.toFixed(2)},${y0.toFixed(2)} A${R},${R} 0 0 1 ${x1.toFixed(2)},${y1.toFixed(2)} Z`;
    return { d, name, i };
  }), [names, seg]);

  const bulbs = useMemo(() => Array.from({ length: 24 }, (_, k) => {
    const a = (k * 15 - 90) * Math.PI / 180;
    return { x: C + 122 * Math.cos(a), y: C + 122 * Math.sin(a), k };
  }), []);

  // piega della lancetta: se il chiamante simula la fisica (pointerBend) usa
  // quella; altrimenti formula geometrica dalla posizione nel giro
  // (segno negativo: la bacchetta la spinge nel verso del giro)
  const segFrac = n ? ((((rotation % seg) + seg) % seg) / seg) : 0;
  const bend = pointerBend ?? (segFrac > 0.72 ? -26 * ((segFrac - 0.72) / 0.28) : 0);

  return (
    <div className="relative mx-auto" style={{ width: cap, maxWidth: "100%" }} data-testid="fortune-wheel">
      <style>{`
        @keyframes fw-blink-a { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0.15; } }
        @keyframes fw-blink-b { 0%, 49% { opacity: 0.15; } 50%, 100% { opacity: 1; } }
        .fw-bulb-a { animation: fw-blink-a 1s steps(1) infinite; }
        .fw-bulb-b { animation: fw-blink-b 1s steps(1) infinite; }
        .fw-winner-slice { filter: drop-shadow(0 0 10px rgba(61,220,132,0.9)); }
      `}</style>
      <svg viewBox="0 0 300 300" className="block w-full h-auto">
        {/* anello esterno */}
        <circle cx={C} cy={C} r={118} fill="#050505" stroke="#2A2A2A" strokeWidth="2" />
        {/* spicchi rotanti */}
        <g transform={`rotate(${rotation} ${C} ${C})`}>
          {slices.length === 0 && (
            <circle cx={C} cy={C} r={R} fill="#0C0C0C" stroke="#1A1A1A" strokeWidth="1.5" />
          )}
          {slices.map((s) => (
            <path key={s.i} d={s.d}
              className={landed && s.i === winnerIndex ? "fw-winner-slice" : ""}
              fill={landed && s.i === winnerIndex ? "#3DDC84"
                : n % 2 === 1 && s.i === n - 1 ? "#9C4200" // con spicchi DISPARI la cucitura ultimo/primo avrebbe due arancioni attaccati: l'ultimo va in arancione scuro
                : s.i % 2 === 0 ? "#FF5C00" : "#161616"}
              stroke="#050505" strokeWidth="1" />
          ))}
          {/* bacchette tra uno spicchio e l'altro: sporgono dal bordo e la lancetta le urta */}
          {slices.map((s) => {
            const a = ((s.i * seg) - 90) * Math.PI / 180;
            const px = C + (R - 2) * Math.cos(a), py = C + (R - 2) * Math.sin(a);
            const sx = C + (R + 7) * Math.cos(a), sy = C + (R + 7) * Math.sin(a);
            return (
              <g key={`peg${s.i}`} data-testid={s.i === 0 ? "wheel-peg" : undefined}>
                <line x1={px} y1={py} x2={sx} y2={sy} stroke="#8A8D91" strokeWidth="2.4" />
                <circle cx={sx} cy={sy} r="3" fill="#E8E8E8" stroke="#050505" strokeWidth="1" />
              </g>
            );
          })}
        </g>
        {/* lucine sopra gli spicchi */}
        {bulbs.map((b) => (
          <circle key={b.k} cx={b.x} cy={b.y} r="3.2"
            className={b.k % 2 === 0 ? "fw-bulb-a" : "fw-bulb-b"}
            fill={b.k % 4 < 2 ? "#FF5C00" : "#F5F5F5"} />
        ))}
        {/* pallino centrale piccolissimo: massimo spazio ai nomi degli spicchi */}
        <circle cx={C} cy={C} r={7} fill="#050505" stroke="#FF5C00" strokeWidth="2" />
        {/* nomi DENTRO gli spicchi, centrati sulla bisettrice: ancorati al bordo
            esterno (2px) e crescono verso il centro — solo i nomi lunghi arrivano dentro */}
        {showNames && (
          <g transform={`rotate(${rotation} ${C} ${C})`}>
            {slices.map((s) => (
              <g key={`t${s.i}`} transform={`rotate(${(s.i + 0.5) * seg - 90} ${C} ${C})`}>
                <text
                  x={C + R - 4} y={C}
                  fontSize={fontSize}
                  fontFamily="'JetBrains Mono', monospace"
                  fontWeight="700"
                  fill="#F5F5F5"
                  stroke="#050505"
                  strokeWidth="0.8"
                  paintOrder="stroke"
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {s.name}
                </text>
              </g>
            ))}
          </g>
        )}
        {/* lancetta in alto: la punta arriva alle bacchette e ci collide fisicamente */}
        <g transform={`rotate(${bend} ${C} ${C - R - 16})`} data-testid="wheel-pointer">
          <path d={`M${C},${C - R + 4} L${C - 10},${C - R - 16} L${C + 10},${C - R - 16} Z`} fill="#F5F5F5" stroke="#050505" strokeWidth="1.5" />
          <circle cx={C} cy={C - R - 14} r="4" fill="#FF5C00" stroke="#050505" strokeWidth="1" />
        </g>
      </svg>
    </div>
  );
}
